import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DepreciationCalculator } from '@/lib/depreciation-calculator';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Play,
  FileText,
  CheckCircle2,
  Clock,
  Sparkles,
  Eye,
  TrendingDown,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Filter,
  Columns,
  X,
  FolderOpen,
  History,
  MoreVertical,
  Download,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AssetSelectionDialog } from '@/components/depreciation/asset-selection-dialog';
import { DepreciationEntryScreen } from '@/components/transactions/depreciation-entry-screen';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import { DemoModeWarningModal } from '@/components/ui/demo-mode-warning-modal';
import { FeatureSettingsGuard } from '@/components/features/FeatureSettingsGuard';
import { ProductTour } from '@/components/ui/product-tour';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { useTour } from '@/contexts/TourContext';
import { useDepreciationTour } from '@/hooks/use-depreciation-tour';
import type { Asset } from '@/types/asset';
import type { DepreciationBatch, DepreciationItem, CurrentPeriodSummary, EligibleAssetForDepreciation } from '@/types/depreciation';
import { mockCurrentPeriod, mockCompletedBatches } from '@/lib/mock-depreciation-data';
import { mockAssets } from '@/lib/mock-data';
import { dataService } from '@/services/data-service';
import { useMode } from '@/providers/ModeProvider';
import { useDepreciationPermissions } from '@/hooks/use-permissions';
import { exportToCSV, exportToExcel, exportToPDF, generateFilename, formatCurrencyForExport, type ExportColumn } from '@/lib/export-utils';
import { formatCurrency } from '@/lib/format-currency';
import { formatApiErrorMessage } from '@/lib/format-utils';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { DepreciationFiltersTab } from '@/components/RightPanel/DepreciationFiltersTab';
import { DepreciationColumnsTab } from '@/components/RightPanel/DepreciationColumnsTab';
import { DEFAULT_DEPRECIATION_COLUMNS } from '@/lib/depreciation-columns-config';

const getDepreciationExportColumns = (): ExportColumn<DepreciationItem>[] => [
  { label: 'Asset Code', key: 'assetCode' as keyof DepreciationItem },
  { label: 'Asset Description', key: 'assetDescription' as keyof DepreciationItem },
  { label: 'Category', key: 'category' as keyof DepreciationItem },
  { label: 'Location', key: 'location' as keyof DepreciationItem },
  { label: 'Opening NBV', key: 'openingNBV' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Acquisition Cost', key: 'acquisitionCost' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Accumulated Depreciation', key: 'accumulatedDepreciation' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Method', key: 'method' as keyof DepreciationItem },
  { label: 'Useful Life (Years)', key: 'usefulLife' as keyof DepreciationItem },
  { label: 'Life Remaining (Years)', key: 'lifeRemaining' as keyof DepreciationItem, format: (val: number) => val?.toFixed(2) ?? '' },
  { label: 'Residual Value', key: 'residualValue' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Depreciation %', key: 'depreciationPercent' as keyof DepreciationItem, format: (val: number) => val ? `${val.toFixed(2)}%` : '' },
  { label: 'Depreciation Value', key: 'depreciationValue' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Closing NBV', key: 'closingNBV' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Manual Override', key: 'isManualOverride' as keyof DepreciationItem, format: (val: boolean) => val ? 'Yes' : 'No' },
  { label: 'Override Reason', key: 'overrideReason' as keyof DepreciationItem },
  { label: 'Notes', key: 'notes' as keyof DepreciationItem },
];

type SortDirection = 'asc' | 'desc' | null;

interface SortIconProps {
  readonly columnKey: string;
  readonly sortKey: string | null;
  readonly sortDirection: SortDirection;
}

function SortIcon({ columnKey, sortKey, sortDirection }: SortIconProps): React.ReactElement | null {
  if (sortKey !== columnKey) {
    return null;
  }
  return sortDirection === 'asc' ? (
    <ArrowUp className="ml-2 h-4 w-4" />
  ) : (
    <ArrowDown className="ml-2 h-4 w-4" />
  );
}

// Helper function to get sortable value from a batch based on the sort key
function getBatchSortValue(batch: DepreciationBatch, key: string): string | number {
  switch (key) {
    case 'batchReference':
      return batch.batchReference;
    case 'dateOfRun':
      return new Date(batch.dateOfRun).getTime();
    case 'assetCount':
      return batch.items?.length ?? 0;
    case 'totalDepreciation':
      return batch.totalDepreciation ?? 0;
    case 'postedBy':
      return batch.postedBy ?? '';
    case 'postedDate':
      return batch.postedDate ? new Date(batch.postedDate).getTime() : 0;
    case 'status':
      return batch.status;
    default:
      return 0;
  }
}

// Helper function to compare two values for sorting
function compareSortValues(aValue: string | number | null, bValue: string | number | null, direction: SortDirection): number {
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return 1;
  if (bValue == null) return -1;
  if (aValue < bValue) return direction === 'asc' ? -1 : 1;
  if (aValue > bValue) return direction === 'asc' ? 1 : -1;
  return 0;
}

// Helper function to generate page numbers for pagination - extracted to reduce nesting depth (S2004)
function generatePageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  const pageNumbers: (number | string)[] = [];
  const maxVisiblePages = 7;
  
  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
  } else {
    pageNumbers.push(1);
    
    if (currentPage > 3) {
      pageNumbers.push('...');
    }
    
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    
    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    
    if (currentPage < totalPages - 2) {
      pageNumbers.push('...');
    }
    
    pageNumbers.push(totalPages);
  }
  
  return pageNumbers;
}

function DepreciationPageContent():React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDemo, setMode } = useMode();
  const { canCreate } = useDepreciationPermissions();
  
  // Product Tour
  const { isTourOpen, tourSteps, startTour, closeTour, completeTour } = useDepreciationTour();
  const { setOnStartTour } = useTour();
  
  // Register tour with context
  useEffect(() => {
    setOnStartTour(() => startTour);
    return () => setOnStartTour(null);
  }, [setOnStartTour, startTour]);
  
  const [showAssetSelection, setShowAssetSelection] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<DepreciationBatch | null>(null);
  const [period, setPeriod] = useState(mockCurrentPeriod);
  const [currentPeriodSummary, setCurrentPeriodSummary] = useState<CurrentPeriodSummary | null>(null);
  const [isLoadingPeriodSummary, setIsLoadingPeriodSummary] = useState(false);
  const [isLoadingEligibleAssets, setIsLoadingEligibleAssets] = useState(false);
  const [completedBatches, setCompletedBatches] = useState<DepreciationBatch[]>(mockCompletedBatches);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showDemoWarning, setShowDemoWarning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc' | null;
  }>({
    key: 'dateOfRun',
    direction: 'desc',
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const pageSize = 25;

  // Register tour with context
  useEffect(() => {
    setOnStartTour(() => startTour);
    return () => setOnStartTour(null);
  }, [setOnStartTour, startTour]);

  useEffect(() => {
    const fetchBatches = async () => {
      setIsLoadingBatches(true);
      try {
        const batches = await dataService.getDepreciationBatches();
        if (batches?.length > 0) {
          const sortedBatches = batches.sort((a: any, b: any) => 
            new Date(b.dateOfRun).getTime() - new Date(a.dateOfRun).getTime()
          );
          setCompletedBatches(sortedBatches);
        } else {
          setCompletedBatches(mockCompletedBatches);
        }
      } catch (error) {
        console.error('Error fetching depreciation batches:', error);
        setCompletedBatches(mockCompletedBatches);
      } finally {
        setIsLoadingBatches(false);
      }
    };

    if (!currentBatch) {
      fetchBatches();
    }
  }, [currentBatch, refreshTrigger]);

  // Fetch current period summary from API
  useEffect(() => {
    const fetchCurrentPeriodSummary = async () => {
      setIsLoadingPeriodSummary(true);
      try {
        const summary = await dataService.getCurrentPeriodDepreciationSummary();
        setCurrentPeriodSummary(summary);
        // Update period display with API data
        setPeriod({
          ...mockCurrentPeriod,
          period: summary.period,
          status: summary.isUpToDate ? 'Completed' : 'Not Started',
          assetsInScope: summary.assetsInScope,
          priorPeriodDepreciation: summary.priorPeriodDepreciation,
          exceptionsLastRun: summary.exceptionsLastRun,
          expectedCompletionBy: summary.expectedCompletionBy,
        });
      } catch (error) {
        console.error('Error fetching current period summary:', error);
        // Fall back to mock data on error
        setCurrentPeriodSummary(null);
      } finally {
        setIsLoadingPeriodSummary(false);
      }
    };

    if (!currentBatch) {
      fetchCurrentPeriodSummary();
    }
  }, [currentBatch, refreshTrigger]);

  // Reset to page 1 when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortConfig]);

  // Handle selected assets passed from assets page and URL parameters
  useEffect(() => {
    const state = location.state as { selectedAssets?: Asset[]; refresh?: boolean; viewBatch?: boolean } | null;
    const searchParams = new URLSearchParams(location.search);
    const batchId = searchParams.get('batch');
    
    // Handle refresh from breadcrumb click (when clicking current page without batch param)
    if (state?.refresh && !batchId) {
      // Clear the current batch and reset page state
      setCurrentBatch(null);
      setSearchQuery('');
      setCurrentPage(1);
      setSortConfig({ key: 'dateOfRun', direction: 'desc' });
      setRefreshTrigger(prev => prev + 1); // Trigger data refetch
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
      return;
    }
    
    // Handle clearing batch when navigating back to main page (no batch param)
    if (!batchId && currentBatch) {
      setCurrentBatch(null);
      setRefreshTrigger(prev => prev + 1);
      return;
    }
    
    if (state?.selectedAssets && state.selectedAssets.length > 0) {
      handleAssetsSelected(state.selectedAssets);
      // Clear the state to prevent re-triggering on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state, location.search]);

  // Sort handler - cycles through: unsorted → asc → desc → unsorted
  const handleSort = (key: string) => {
    setSortConfig((prevConfig) => {
      // If clicking a different column, start with asc
      if (prevConfig.key !== key) {
        return { key, direction: 'asc' };
      }
      // If same column, cycle through: asc → desc → unsorted
      if (prevConfig.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (prevConfig.direction === 'desc') {
        return { key: null, direction: null };
      }
      // If unsorted, start with asc
      return { key, direction: 'asc' };
    });
  };

  const handleStartRun = () => {
    // In demo mode, allow starting runs with mock data for demonstration purposes
    setShowAssetSelection(true);
  };

  const handleStartRunWithAllAssets = async () => {
    // In demo mode, use mock assets for demonstration purposes
    if (isDemo) {
      handleAssetsSelected(mockAssets);
      return;
    }
    
    // Fetch eligible assets from API and start the run with pre-calculated values
    setIsLoadingEligibleAssets(true);
    try {
      const apiEligibleAssets = await dataService.getCurrentPeriodEligibleAssets();
      
      if (apiEligibleAssets.length === 0) {
        toast.info('No eligible assets', {
          description: 'No assets are eligible for depreciation in the current period.',
        });
        return;
      }
      
      // Convert API eligible assets to Asset format for handleAssetsSelected
      // The API returns pre-calculated depreciation values which we'll use
      const assetsWithCalculations: Asset[] = apiEligibleAssets.map((eligibleAsset: EligibleAssetForDepreciation) => ({
        id: eligibleAsset.id,
        code: eligibleAsset.assetId,
        name: eligibleAsset.name,
        description: eligibleAsset.description ?? '',
        category: eligibleAsset.category,
        location: eligibleAsset.location,
        status: eligibleAsset.status as any,
        acquisitionCost: eligibleAsset.acquisitionCost,
        accumulatedDepreciation: eligibleAsset.accumulatedDepreciation,
        netBookValue: eligibleAsset.netBookValue,
        residualValue: eligibleAsset.residualValue,
        depreciationMethod: eligibleAsset.depreciationMethod as any,
        usefulLife: eligibleAsset.usefulLife ?? 5,
        acquisitionDate: eligibleAsset.acquisitionDate ?? '',
        // Store pre-calculated values in a way handleAssetsSelected can use
        // These will be used by the DepreciationCalculator or overridden
        _calculatedDepreciationValue: eligibleAsset.calculatedDepreciationValue,
        _calculatedClosingNBV: eligibleAsset.calculatedClosingNBV,
        _calculatedDepreciationPercent: eligibleAsset.calculatedDepreciationPercent,
        _formula: eligibleAsset.formula,
        _formulaBreakdown: eligibleAsset.formulaBreakdown,
      } as Asset & {
        _calculatedDepreciationValue: number;
        _calculatedClosingNBV: number;
        _calculatedDepreciationPercent?: number;
        _formula?: string;
        _formulaBreakdown?: string;
      }));
      
      // Create depreciation items directly with pre-calculated values from API
      handleAssetsSelectedWithCalculations(assetsWithCalculations, apiEligibleAssets);
    } catch (error) {
      console.error('Error fetching eligible assets:', error);
      toast.error('Failed to fetch eligible assets', {
        description: 'Please try again or use the asset selection dialog.',
      });
      // Fall back to mock assets on error
      handleAssetsSelected(mockAssets);
    } finally {
      setIsLoadingEligibleAssets(false);
    }
  };

  const isServerGeneratedId = (id: string) => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(id);
  };

  const handleViewLastBatchSummary = async () => {
    if (!completedBatches || completedBatches.length === 0) {
      toast.info('No batch history available', {
        description: 'There are no completed depreciation batches to view.',
      });
      return;
    }

    try {
      // Get the most recent batch (already sorted by date descending)
      const lastBatch = completedBatches[0];
      
      // Navigate to the batch view URL
      navigate(`/transactions/depreciation?batch=${lastBatch.id}`, {
        state: { viewBatch: true }
      });
      
      // If it's a server-generated ID, fetch the full batch details
      if (isServerGeneratedId(lastBatch.id)) {
        try {
          const fullBatch = await dataService.getDepreciationBatch(lastBatch.id);
          setCurrentBatch({
            ...fullBatch,
            dateOfRun: fullBatch.dateOfRun?.split('T')[0] ?? fullBatch.dateOfRun,
          });
          toast.success('Batch loaded', {
            description: `Viewing batch: ${fullBatch.batchReference ?? 'Untitled'}`,
          });
        } catch (error) {
          console.error('Failed to fetch batch details:', error);
          // Fallback to using the batch from the list
          setCurrentBatch({
            ...lastBatch,
                  dateOfRun: lastBatch.dateOfRun?.split('T')[0] ?? lastBatch.dateOfRun,
                });
                toast.success('Batch loaded', {
                  description: `Viewing batch: ${lastBatch.batchReference ?? 'Untitled'}`,
                });
              }
            } else {
              // Use the batch directly from the list
              setCurrentBatch({
                ...lastBatch,
                dateOfRun: lastBatch.dateOfRun?.split('T')[0] ?? lastBatch.dateOfRun,
              });
              toast.success('Batch loaded', {
                description: `Viewing batch: ${lastBatch.batchReference ?? 'Untitled'}`,
              });
            }
    } catch (error) {
      console.error('Error loading last batch:', error);
      toast.error('Failed to load batch', {
        description: 'An error occurred while loading the batch.',
      });
    }
  };



  const handleAssetsSelected = (selectedAssets: Asset[]) => {
    // Create depreciation items from selected assets
    const newItems: DepreciationItem[] = selectedAssets.map((asset, index) => {
      // Use the DepreciationCalculator to calculate depreciation based on asset's method
      const calculation = DepreciationCalculator.calculateDepreciation(asset, new Date(), 12);
      
      const usefulLifeYears = asset.usefulLife ?? 5;
      const depreciationMethod = asset.depreciationMethod ?? 'Straight Line';

      return {
        id: `dep-item-${Date.now()}-${index}`,
        assetId: asset.id,
        assetCode: asset.code,
        assetDescription: asset.name,
        category: asset.category,
        location: asset.location,
        openingNBV: asset.netBookValue,
        acquisitionCost: asset.acquisitionCost,
        accumulatedDepreciation: asset.accumulatedDepreciation,
        method: depreciationMethod as any,
        usefulLife: usefulLifeYears,
        lifeRemaining: usefulLifeYears - (asset.accumulatedDepreciation / asset.acquisitionCost) * usefulLifeYears,
        residualValue: asset.residualValue ?? 0,
        aiDepreciationPercent: calculation.depreciationPercent ?? 0,
        aiDepreciationValue: calculation.depreciationValue,
        aiClosingNBV: calculation.closingNBV,
        confidence: 'High' as const,
        depreciationPercent: calculation.depreciationPercent ?? 0,
        depreciationValue: calculation.depreciationValue,
        closingNBV: calculation.closingNBV,
        isManualOverride: false,
        validationStatus: 'valid',
        calculationInputs: calculation.calculationInputs ?? {
          method: depreciationMethod,
          residualValue: asset.residualValue ?? 0,
          formula: calculation.formula,
          formulaBreakdown: calculation.formulaBreakdown ?? '',
        },
      };
    });

    // If we have an existing batch, add to it; otherwise create new batch
    if (currentBatch) {
      // Filter out duplicates based on assetId
      const existingAssetIds = new Set(currentBatch.items.map(item => item.assetId));
      const uniqueNewItems = newItems.filter(item => !existingAssetIds.has(item.assetId));
      
      const allItems = [...currentBatch.items, ...uniqueNewItems];
      const totalDepreciation = allItems.reduce((sum, item) => sum + item.depreciationValue, 0);
      const totalOpeningNBV = allItems.reduce((sum, item) => sum + item.openingNBV, 0);
      const totalClosingNBV = allItems.reduce((sum, item) => sum + item.closingNBV, 0);
      
      setCurrentBatch({
        ...currentBatch,
        items: allItems,
        totalDepreciation,
        totalOpeningNBV,
        totalClosingNBV,
      });
    } else {
      // Create new batch
      const totalDepreciation = newItems.reduce((sum, item) => sum + item.depreciationValue, 0);
      const totalOpeningNBV = newItems.reduce((sum, item) => sum + item.openingNBV, 0);
      const totalClosingNBV = newItems.reduce((sum, item) => sum + item.closingNBV, 0);

      const batch: DepreciationBatch = {
        id: `batch-${Date.now()}`,
        batchReference: '',
        dateOfRun: new Date().toISOString().split('T')[0],
        projectId: '',
        projectName: '',
        status: 'In Progress',
        notes: '',
        items: newItems,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        modifiedBy: 'Current User',
        modifiedDate: new Date().toISOString(),
        preparedBy: 'Current User',
        preparedDate: new Date().toISOString(),
        currentStep: 'Prepare',
        totalDepreciation,
        totalOpeningNBV,
        totalClosingNBV,
        currency: 'GBP',
      };

      setCurrentBatch(batch);
    }
    
    setShowAssetSelection(false);
  };

  // Handle assets selected with pre-calculated depreciation values from API
  const handleAssetsSelectedWithCalculations = (
    selectedAssets: Asset[],
    eligibleAssetsData: EligibleAssetForDepreciation[]
  ) => {
    // Create a map for quick lookup of pre-calculated values
    const calculationsMap = new Map(
      eligibleAssetsData.map((ea) => [ea.id, ea])
    );

    // Create depreciation items using pre-calculated values from API
    const newItems: DepreciationItem[] = selectedAssets.map((asset, index) => {
      const apiCalc = calculationsMap.get(asset.id);
      const usefulLifeYears = asset.usefulLife ?? 5;
      const depreciationMethod = asset.depreciationMethod ?? 'Straight Line';

      // Use pre-calculated values from API if available, otherwise fall back to calculator
      const depreciationValue = apiCalc?.calculatedDepreciationValue ?? 0;
      const closingNBV = apiCalc?.calculatedClosingNBV ?? asset.netBookValue;
      const depreciationPercent = apiCalc?.calculatedDepreciationPercent ?? 0;
      
      // Use the API's calculationInputs for "Why this value?" display
      // This ensures the calculation breakdown matches the actual values shown in the row
      // Only fall back to frontend calculator if API doesn't provide calculationInputs
      let calculationInputs = apiCalc?.calculationInputs;
      if (!calculationInputs) {
        // Fall back to frontend calculator only if API doesn't provide calculationInputs
        const calculation = DepreciationCalculator.calculateDepreciation(asset, new Date(), 12);
        calculationInputs = calculation.calculationInputs ?? {
          method: depreciationMethod,
          residualValue: asset.residualValue ?? 0,
          formula: apiCalc?.formula ?? '',
          formulaBreakdown: apiCalc?.formulaBreakdown ?? '',
        };
      }

      return {
        id: `dep-item-${Date.now()}-${index}`,
        assetId: asset.id,
        assetCode: asset.code,
        assetDescription: asset.name,
        category: asset.category,
        location: asset.location,
        openingNBV: asset.netBookValue,
        acquisitionCost: asset.acquisitionCost,
        accumulatedDepreciation: asset.accumulatedDepreciation,
        method: depreciationMethod as any,
        usefulLife: usefulLifeYears,
        lifeRemaining: usefulLifeYears - (asset.accumulatedDepreciation / asset.acquisitionCost) * usefulLifeYears,
        residualValue: asset.residualValue ?? 0,
        aiDepreciationPercent: depreciationPercent,
        aiDepreciationValue: depreciationValue,
        aiClosingNBV: closingNBV,
        confidence: 'High' as const,
        depreciationPercent: depreciationPercent,
        depreciationValue: depreciationValue,
        closingNBV: closingNBV,
        isManualOverride: false,
        validationStatus: 'valid',
        calculationInputs,
      };
    });

    // Create new batch with pre-calculated items
    const totalDepreciation = newItems.reduce((sum, item) => sum + item.depreciationValue, 0);
    const totalOpeningNBV = newItems.reduce((sum, item) => sum + item.openingNBV, 0);
    const totalClosingNBV = newItems.reduce((sum, item) => sum + item.closingNBV, 0);

    const batch: DepreciationBatch = {
      id: `batch-${Date.now()}`,
      batchReference: '',
      dateOfRun: new Date().toISOString().split('T')[0],
      projectId: '',
      projectName: '',
      status: 'In Progress',
      notes: '',
      items: newItems,
      createdBy: 'Current User',
      createdDate: new Date().toISOString(),
      modifiedBy: 'Current User',
      modifiedDate: new Date().toISOString(),
      preparedBy: 'Current User',
      preparedDate: new Date().toISOString(),
      currentStep: 'Prepare',
      totalDepreciation,
      totalOpeningNBV,
      totalClosingNBV,
      currency: currentPeriodSummary?.currency ?? 'GBP',
    };

    setCurrentBatch(batch);
    setShowAssetSelection(false);
  };



  const handleExportExcel = () => {
    if (!currentBatch || !currentBatch.items || currentBatch.items.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      const columns: ExportColumn<DepreciationItem>[] = [
        { label: 'Asset Code', key: 'assetCode' as keyof DepreciationItem },
        { label: 'Asset Description', key: 'assetDescription' as keyof DepreciationItem },
        { label: 'Category', key: 'category' as keyof DepreciationItem },
        { label: 'Location', key: 'location' as keyof DepreciationItem },
        { label: 'Opening NBV', key: 'openingNBV' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Acquisition Cost', key: 'acquisitionCost' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Accumulated Depreciation', key: 'accumulatedDepreciation' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Method', key: 'method' as keyof DepreciationItem },
        { label: 'Useful Life (Years)', key: 'usefulLife' as keyof DepreciationItem },
        { label: 'Life Remaining (Years)', key: 'lifeRemaining' as keyof DepreciationItem, format: (val: number) => val.toFixed(2) },
        { label: 'Residual Value', key: 'residualValue' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Depreciation %', key: 'depreciationPercent' as keyof DepreciationItem, format: (val: number) => val ? `${val.toFixed(2)}%` : '' },
        { label: 'Depreciation Value', key: 'depreciationValue' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Closing NBV', key: 'closingNBV' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Manual Override', key: 'isManualOverride' as keyof DepreciationItem, format: (val: boolean) => val ? 'Yes' : 'No' },
        { label: 'Override Reason', key: 'overrideReason' as keyof DepreciationItem },
        { label: 'Notes', key: 'notes' as keyof DepreciationItem },
      ];

      exportToExcel({
        data: currentBatch.items,
        columns,
        filename: generateFilename(`depreciation-batch-${currentBatch.batchReference ?? 'draft'}`, 'xls'),
        sheetName: 'Depreciation Details',
      });

      toast.success('Export successful', {
        description: 'Depreciation batch exported to Excel',
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  const handleExportPDF = () => {
    if (!currentBatch || !currentBatch.items || currentBatch.items.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      const columns: ExportColumn<DepreciationItem>[] = [
        { label: 'Asset Code', key: 'assetCode' as keyof DepreciationItem },
        { label: 'Description', key: 'assetDescription' as keyof DepreciationItem },
        { label: 'Category', key: 'category' as keyof DepreciationItem },
        { label: 'Opening NBV', key: 'openingNBV' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Depreciation', key: 'depreciationValue' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Closing NBV', key: 'closingNBV' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Method', key: 'method' as keyof DepreciationItem },
      ];

      exportToPDF({
        data: currentBatch.items,
        columns,
        filename: generateFilename(`depreciation-batch-${currentBatch.batchReference ?? 'draft'}`, 'pdf'),
        title: 'Depreciation Batch Report',
        subtitle: `Batch Reference: ${currentBatch.batchReference ?? 'Draft'} | Period: ${currentBatch.period ?? '-'}/${currentBatch.year ?? '-'}`,
        orientation: 'landscape',
      });

      toast.success('Export successful', {
        description: 'Depreciation batch exported to PDF',
      });
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  // Use API data for period summary if available, otherwise fall back to mock calculations
  const estimatedPeriodDepreciation = currentPeriodSummary?.totalDepreciation ?? mockAssets.reduce((sum: number, asset: Asset) => {
    const usefulLifeYears = 5;
    const monthlyRate = 1 / (usefulLifeYears * 12);
    const depreciation = (asset.netBookValue - (asset.residualValue ?? 0)) * monthlyRate;
    return sum + depreciation;
  }, 0);

  // Use API data for current total NBV if available
  const currentTotalNBV = currentPeriodSummary?.totalCurrentNBV ?? mockAssets.reduce((sum: number, asset: Asset) => sum + asset.netBookValue, 0);
  
  // Use API data for expected NBV if available
  const expectedTotalNBV = currentPeriodSummary?.totalExpectedNBV ?? (currentTotalNBV - estimatedPeriodDepreciation);
  
  // Use API data for assets in scope count
  const assetsInScopeCount = currentPeriodSummary?.assetsInScope ?? mockAssets.length;
  
  // Check if period is up to date (no assets eligible)
  const isPeriodUpToDate = currentPeriodSummary?.isUpToDate ?? false;

  // Right Panel Configuration
  const { visibleColumns, columnOrder, setVisibleColumns, setColumnOrder, activeFilterCount } = useRightPanel();
  
  // Initialize columns on mount
  useEffect(() => {
    if (visibleColumns.length === 0) {
      const defaultVisible = DEFAULT_DEPRECIATION_COLUMNS
        .filter(col => col.visible)
        .map(col => col.id);
      setVisibleColumns(defaultVisible);
    }
    if (columnOrder.length === 0) {
      setColumnOrder(DEFAULT_DEPRECIATION_COLUMNS.map(col => col.id));
    }
  }, [visibleColumns.length, columnOrder.length, setVisibleColumns, setColumnOrder]);

  // Right Panel Tabs
  const rightPanelTabs: RightPanelTab[] = [
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: <DepreciationFiltersTab />,
      showBadge: true,
      badgeCount: activeFilterCount,
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns,
      content: <DepreciationColumnsTab />,
    },
  ];

  // Landing Dashboard View
  if (!currentBatch) {
    const content = (
      <div className="flex h-full flex-col">
        {/* Page Header */}
        <div className="border-b bg-card p-6 flex-shrink-0" data-tour="depreciation-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <TrendingDown className="h-6 w-6 text-primary" />
                Depreciations
              </h1>
              <p className="text-sm text-muted-foreground mt-1">View, create, or edit transactions that reduce asset net book value over time.</p>
            </div>
                    <div className="flex items-center gap-2">
                      <EvoFeatureFeedbackCollector feature="Depreciation Transactions" />
                      <Button variant="outline" onClick={handleViewLastBatchSummary} data-tour="view-summary-button">
                        <Eye className="mr-2 h-4 w-4" />
                        View Last Batch Summary
                      </Button>
                      {canCreate && (
                        <Button onClick={handleStartRun} data-tour="start-batch-button">
                          <Play className="mr-2 h-4 w-4" />
                          Start New Depreciation Run
                        </Button>
                      )}
                    </div>
                    </div>
                  </div>

                {/* Content */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="p-6 pb-0 flex-shrink-0">
        {/* Current Period Status - Wrapped with gradient border */}
        <div data-tour="current-period">
          <div 
            className="rounded-xl p-[5px]"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            }}
          >
            <Card className="border-0 rounded-[10px] relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/5 opacity-40" />
              <CardContent className="relative p-6 space-y-6">
                <div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-semibold">Current Period: {period.period}</h2>
                      <Sparkles className="h-5 w-5 text-purple-500" />
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Suggested depreciation run for current period</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-4 right-4 h-8 w-8"
                    onClick={() => toast.info('Close functionality coming soon')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                {isPeriodUpToDate ? (
                  <div className="flex items-center gap-4">
                    <CheckCircle2 className="h-12 w-12 text-green-500 flex-shrink-0" />
                    <div className="text-right">
                      <div className="text-lg font-semibold text-green-700 dark:text-green-400">
                        {currentPeriodSummary?.upToDateMessage ?? 'No more assets are marked for depreciation this period.'}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        No more assets are marked for depreciation this period
                      </p>
                    </div>
                  </div>
                ): isLoadingPeriodSummary ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="text-center">
                      <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2 animate-pulse" />
                      <p className="text-sm text-muted-foreground">Loading period summary...</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4 2xl:gap-6">
                    <div className="space-y-1">
                      <div className="text-xs 2xl:text-sm text-muted-foreground">Assets in Scope</div>
                      <div className="text-lg 2xl:text-2xl font-bold">{assetsInScopeCount.toLocaleString()}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs 2xl:text-sm text-muted-foreground">Current NBV</div>
                      <div className="text-lg 2xl:text-2xl font-bold">£{currentTotalNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs 2xl:text-sm text-muted-foreground">Expected NBV</div>
                      <div className="text-lg 2xl:text-2xl font-bold">
                        £{expectedTotalNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        <span className="text-xs 2xl:text-sm text-red-600 ml-1 2xl:ml-2 block 2xl:inline">
                          - £{estimatedPeriodDepreciation.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {period.status === 'Not Started' && !isPeriodUpToDate && (
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-end">
                    <Button 
                      size="lg" 
                      onClick={handleStartRunWithAllAssets}
                      disabled={isLoadingEligibleAssets || isLoadingPeriodSummary}
                    >
                      {isLoadingEligibleAssets ? (
                        <>
                          <Clock className="mr-2 h-5 w-5 animate-spin" />
                          Loading Assets...
                        </>
                      ) : (
                        <>
                          <Play className="mr-2 h-5 w-5" />
                          Start Depreciation Run
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
              
              {period.status === 'Completed' && period.completedBy && (
                <div className="pt-4 border-t">
                  <div className="text-sm text-muted-foreground">Completed</div>
                  <div className="text-sm font-medium">
                    {period.completedBy} on {period.completedDate && new Date(period.completedDate).toLocaleString('en-GB')}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        </div>
        </div>

        {/* Transaction History Header */}
        <div className="bg-white dark:bg-black border-t border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Transaction History</h2>
              <p className="text-sm text-muted-foreground mt-1">View and manage all depreciation transactions</p>
            </div>
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white dark:bg-slate-900"
                data-tour="search-transactions"
              />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="flex-1 flex flex-col min-h-0">
          
          {(() => {
            if (isLoadingBatches) {
              return (
                <div className="flex h-64 items-center justify-center">
                  <div className="text-center">
                    <p className="text-lg font-medium text-slate-700 dark:text-slate-300">Loading transactions...</p>
                  </div>
                </div>
              );
            }
            
            // Filter batches
            let filteredBatches = completedBatches.filter((batch) => {
              if (!searchQuery) return true;
              const query = searchQuery.toLowerCase();
              return (
                batch.batchReference.toLowerCase().includes(query) ||
                batch.status.toLowerCase().includes(query) ||
                (batch.postedBy && batch.postedBy.toLowerCase().includes(query))
              );
            });
            
            // Sort batches
            filteredBatches = [...filteredBatches].sort((a, b) => {
              if (!sortConfig.key || !sortConfig.direction) return 0;
              const aValue = getBatchSortValue(a, sortConfig.key);
              const bValue = getBatchSortValue(b, sortConfig.key);
              return compareSortValues(aValue, bValue, sortConfig.direction);
            });
            
            // Pagination calculations
            const totalPages = Math.ceil(filteredBatches.length / pageSize);
            const startIndex = (currentPage - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const paginatedBatches = filteredBatches.slice(startIndex, endIndex);
            
            if (filteredBatches.length === 0) {
              return (
                <div className="flex h-64 items-center justify-center">
                  <div className="text-center space-y-3">
                    <div className="flex justify-center">
                      <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-4">
                        <FileText className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">No transactions found</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {searchQuery ? "Try adjusting your search" : "No depreciation transactions available yet"}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            
            return (
              <div className="flex-1 flex flex-col min-h-0" data-tour="transaction-history">
                <div className="flex-1 overflow-auto">
                <Table className="w-full">
                  <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                    <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('batchReference')}
                        >
                          Reference
                          <SortIcon columnKey="batchReference" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>

                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('dateOfRun')}
                        >
                          Date
                          <SortIcon columnKey="dateOfRun" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('period')}
                        >
                          Period
                          <SortIcon columnKey="period" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('year')}
                        >
                          Year
                          <SortIcon columnKey="year" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('assetCount')}
                        >
                          Assets
                          <SortIcon columnKey="assetCount" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-mr-3 h-8 data-[state=open]:bg-accent ml-auto flex hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('totalDepreciation')}
                        >
                          Total Depreciation
                          <SortIcon columnKey="totalDepreciation" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('postedBy')}
                        >
                          Posted By
                          <SortIcon columnKey="postedBy" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('postedDate')}
                        >
                          Posted Date
                          <SortIcon columnKey="postedDate" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('status')}
                        >
                          Status
                          <SortIcon columnKey="status" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedBatches.map((batch, index) => (
                      <TableRow 
                        key={batch.id}
                        className={`
                          ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                          hover:bg-slate-100 dark:hover:bg-slate-800 
                          border-b border-slate-100 dark:border-slate-800
                          transition-colors
                        `}
                        data-tour={index === 0 ? "batch-calculations" : undefined}
                      >
                        <TableCell className="font-medium text-slate-900 dark:text-slate-100">
                          {batch.batchReference}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">
                          {new Date(batch.dateOfRun).toLocaleDateString('en-GB')}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">
                          {batch.period ?? '-'}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">
                          {batch.year ?? '-'}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">
                          {batch.items?.length ?? 0}
                        </TableCell>
                        <TableCell className="text-right text-slate-700 dark:text-slate-300 tabular-nums">
                          {formatCurrency(batch.totalDepreciation)}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">
                          {batch.postedBy ?? '-'}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">
                          {batch.postedDate 
                            ? new Date(batch.postedDate).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="secondary"
                            className={
                              batch.status === 'In Progress'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            }
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            {batch.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  const formattedBatch = {
                                    ...batch,
                                    dateOfRun: batch.dateOfRun.split('T')[0]
                                  };
                                  setCurrentBatch(formattedBatch);
                                }}
                              >
                                <FolderOpen className="h-4 w-4 mr-2" />
                                Open
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  navigate(`/enquiry?reference=${encodeURIComponent(batch.batchReference)}&transactionType=Depreciation`);
                                }}
                                title="View this transaction in Transaction History"
                              >
                                <History className="h-4 w-4 mr-2" />
                                View Transaction History
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="outline" size="icon">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    if (!batch.items || batch.items.length === 0) { toast.error('No data to export'); return; }
                                    exportToCSV({ data: batch.items, columns: getDepreciationExportColumns(), filename: generateFilename(`depreciation-batch-${batch.batchReference || 'draft'}`, 'csv') });
                                  }}>
                                    <Download className="mr-2 h-4 w-4" />
                                    Export CSV
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    if (!batch.items || batch.items.length === 0) { toast.error('No data to export'); return; }
                                    exportToExcel({ data: batch.items, columns: getDepreciationExportColumns(), filename: generateFilename(`depreciation-batch-${batch.batchReference || 'draft'}`, 'xlsx'), sheetName: 'Depreciation Details' });
                                  }}>
                                    <Download className="mr-2 h-4 w-4" />
                                    Export Excel
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
                
                {/* Pagination Footer - Always visible */}
                <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-black">
                  <div className="text-sm text-muted-foreground flex-shrink-0">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredBatches.length)} of {filteredBatches.length} transactions
                    {filteredBatches.length !== completedBatches.length && ` (filtered from ${completedBatches.length} total)`}
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      
                      {/* Page Number Buttons */}
                      <div className="flex items-center gap-1">
                        {generatePageNumbers(currentPage, totalPages).map((page, pageIndex, pageNumbers) => {
                          if (page === '...') {
                            return (
                              <span key={`ellipsis-before-${pageNumbers[pageIndex + 1]}`} className="px-2 text-muted-foreground">
                                ...
                              </span>
                            );
                          }
                          
                          return (
                            <Button
                              key={`page-${page}`}
                              variant={currentPage === page ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCurrentPage(page as number)}
                              className="min-w-[36px]"
                            >
                              {page}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        Last
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
        </div>

        <AssetSelectionDialog
          open={showAssetSelection}
          onOpenChange={setShowAssetSelection}
          onConfirm={handleAssetsSelected}
          excludeAssetIds={[]}
        />

        <ComingSoonModal
          open={showComingSoon}
          onOpenChange={setShowComingSoon}
          title="Run Summary Coming Soon"
          description="The ability to view the last run summary will be available in an upcoming release."
        />

        <DemoModeWarningModal
          open={showDemoWarning}
          onOpenChange={setShowDemoWarning}
          onSwitchToLive={() => setMode('live')}
        />

        {/* Product Tour */}
        <ProductTour
          steps={tourSteps}
          isOpen={isTourOpen}
          onClose={closeTour}
          onComplete={completeTour}
        />
      </div>
    );

    return (
      <PageWithRightPanel>
        {content}
        <RightPanelBase tabs={rightPanelTabs} />
      </PageWithRightPanel>
    );
  }

  // Batch Processing View - Use the new entry screen component
  return (
    <DepreciationEntryScreen
      batch={currentBatch}
      onClose={() => {
        setCurrentBatch(null);
        setRefreshTrigger(prev => prev + 1);
      }}
      onSave={(updatedBatch) => {
        setCurrentBatch(updatedBatch);
        setRefreshTrigger(prev => prev + 1);
      }}
    />
  );
}

export default function DepreciationPage(): React.ReactElement {
  return (
    <FeatureSettingsGuard>
      <DepreciationPageContent />
    </FeatureSettingsGuard>
  );
}
